import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Download, ArrowLeft, FileText, Star, TrendingUp, Activity,
    Bone, Heart, Target, Award, AlertTriangle, CheckCircle, Trophy, Zap
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const SampleReportPage = () => {
    const SAMPLE = {
        athlete: {
            name: 'Aisha Mwangi',
            sport: 'Basketball',
            position: 'Shooting Guard',
            age: 17,
            country: 'Kenya',
            school: 'Alliance High School',
            tier: 'Elite',
            avatar_initial: 'AM'
        },
        scores: {
            overall: 88,
            biomech: 85,
            tracking: 91,
            consistency: 86,
            potential: 92
        },
        radar: [
            { axis: 'Speed', value: 89, full: 100 },
            { axis: 'Power', value: 82, full: 100 },
            { axis: 'Accuracy', value: 91, full: 100 },
            { axis: 'Stamina', value: 78, full: 100 },
            { axis: 'Reaction', value: 87, full: 100 },
            { axis: 'Agility', value: 90, full: 100 },
        ],
        moments: [
            { time: '0:04', label: 'Drive to Basket', score: 92, type: 'success' },
            { time: '0:11', label: 'Three-point Shot', score: 88, type: 'success' },
            { time: '0:18', label: 'Defensive Steal', score: 85, type: 'info' },
            { time: '0:24', label: 'Rebound', score: 79, type: 'info' },
            { time: '0:31', label: 'Pass Assist', score: 94, type: 'success' },
        ],
        injuryRisks: [
            { area: 'Knee Valgus (Left)', severity: 'low', detail: '12% inward collapse on landing — within acceptable range' },
            { area: 'Ankle Drift (Right)', severity: 'medium', detail: 'Mild lateral drift on push-off — recommend ankle stability work' },
            { area: 'Shoulder Asymmetry', severity: 'low', detail: 'Marginal 3.2° asymmetry — monitor over time' },
        ],
        biomech: {
            avg_jump_height: 58,
            sprint_top_speed: 6.2,
            reaction_time: 0.34,
            stride_length: 1.85,
        },
        tracking: {
            distance_covered: 1842,
            top_speed: 6.2,
            avg_speed: 3.8,
            sprint_count: 14,
        },
        recommendations: [
            'Increase plyometric volume to improve vertical jump (current 58cm → target 65cm)',
            'Add unilateral ankle stability drills 2x/week to address right ankle drift',
            'Maintain current shooting form — accuracy is in 95th percentile',
            'Recovery: 48hr rest cycle after high-intensity sessions for optimal adaptation'
        ]
    };

    const SeverityBadge = ({ level }) => {
        const styles = {
            low: 'bg-green-500/20 text-green-400 border-green-500/40',
            medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
            high: 'bg-red-500/20 text-red-400 border-red-500/40',
        };
        return <span className={`text-xs px-2 py-0.5 rounded border ${styles[level]} capitalize`} data-testid={`severity-${level}`}>{level}</span>;
    };

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Header */}
            <div className="border-b border-white/10 bg-[#0d2847] sticky top-0 z-10 backdrop-blur-md">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/how-it-works"><Button variant="ghost" size="sm" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button></Link>
                        <div>
                            <div className="text-xs text-muted-foreground">SAMPLE REPORT</div>
                            <div className="font-semibold">NextGen Vision Performance Report</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" className="border-white/20" size="sm" data-testid="download-pdf-btn"
                            onClick={() => window.alert('Sample PDF — connect /api/video-analysis/{id}/report/pdf for live downloads')}>
                            <Download className="w-4 h-4 mr-2" />Download PDF
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
                {/* Athlete Card */}
                <Card className="bg-gradient-to-r from-purple-500/10 via-cyan-500/10 to-purple-500/10 border-purple-500/30 p-6">
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center text-3xl font-bold">
                            {SAMPLE.athlete.avatar_initial}
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <h1 className="text-2xl font-bold flex items-center gap-2">
                                {SAMPLE.athlete.name}
                                <span className="text-xs px-2 py-0.5 bg-purple-500/30 text-purple-200 rounded-full">{SAMPLE.athlete.tier}</span>
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                {SAMPLE.athlete.sport} · {SAMPLE.athlete.position} · Age {SAMPLE.athlete.age} · {SAMPLE.athlete.country}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">{SAMPLE.athlete.school}</p>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Overall</div>
                            <div className="text-5xl font-bold text-cyan-400" data-testid="overall-score">{SAMPLE.scores.overall}</div>
                            <div className="text-xs text-muted-foreground">/ 100</div>
                        </div>
                    </div>
                </Card>

                {/* Score Tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        { label: 'Biomechanics', value: SAMPLE.scores.biomech, icon: Bone, color: 'cyan' },
                        { label: 'Player Tracking', value: SAMPLE.scores.tracking, icon: Activity, color: 'purple' },
                        { label: 'Consistency', value: SAMPLE.scores.consistency, icon: Target, color: 'green' },
                        { label: 'Elite Potential', value: SAMPLE.scores.potential, icon: TrendingUp, color: 'yellow' },
                    ].map((s, i) => (
                        <Card key={i} className="bg-[#0d2847] border-white/10 p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <s.icon className={`w-4 h-4 text-${s.color}-400`} />
                                <span className="text-xs text-muted-foreground">{s.label}</span>
                            </div>
                            <div className={`text-3xl font-bold text-${s.color}-400`}>{s.value}</div>
                            <div className="h-1 bg-white/10 rounded mt-2">
                                <div className={`h-full rounded bg-${s.color}-400`} style={{ width: `${s.value}%` }} />
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Radar + Key Moments */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="bg-[#0d2847] border-white/10 p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 text-yellow-400" /> Performance Profile
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <RadarChart data={SAMPLE.radar}>
                                <PolarGrid stroke="#ffffff20" />
                                <PolarAngleAxis dataKey="axis" stroke="#aaa" fontSize={12} />
                                <PolarRadiusAxis stroke="#666" angle={90} domain={[0, 100]} />
                                <Radar dataKey="value" stroke="#a855f7" fill="#a855f7" fillOpacity={0.6} strokeWidth={2} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card className="bg-[#0d2847] border-white/10 p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Zap className="w-5 h-5 text-cyan-400" /> Key Moments (AI-tagged)
                        </h3>
                        <div className="space-y-2" data-testid="key-moments">
                            {SAMPLE.moments.map((m, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-[#0a1628] rounded border-l-2 border-cyan-400">
                                    <div className="flex items-center gap-3">
                                        <code className="text-xs px-2 py-0.5 bg-white/10 rounded">{m.time}</code>
                                        <span className="text-sm font-medium">{m.label}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">Score</span>
                                        <span className="text-lg font-bold text-cyan-400">{m.score}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Biomechanics & Tracking Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="bg-[#0d2847] border-white/10 p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Bone className="w-5 h-5 text-cyan-400" /> Biomechanics
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <StatTile label="Vertical Jump" value={`${SAMPLE.biomech.avg_jump_height} cm`} />
                            <StatTile label="Sprint Top Speed" value={`${SAMPLE.biomech.sprint_top_speed} m/s`} />
                            <StatTile label="Reaction Time" value={`${SAMPLE.biomech.reaction_time}s`} />
                            <StatTile label="Stride Length" value={`${SAMPLE.biomech.stride_length} m`} />
                        </div>
                    </Card>

                    <Card className="bg-[#0d2847] border-white/10 p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                            <Activity className="w-5 h-5 text-purple-400" /> Player Tracking
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <StatTile label="Distance" value={`${SAMPLE.tracking.distance_covered} m`} />
                            <StatTile label="Top Speed" value={`${SAMPLE.tracking.top_speed} m/s`} />
                            <StatTile label="Avg Speed" value={`${SAMPLE.tracking.avg_speed} m/s`} />
                            <StatTile label="Sprints" value={SAMPLE.tracking.sprint_count} />
                        </div>
                    </Card>
                </div>

                {/* Movement Heatmap */}
                <Card className="bg-[#0d2847] border-white/10 p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5 text-red-400" /> Court Coverage Heatmap
                    </h3>
                    <CourtHeatmap />
                    <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-muted-foreground">
                        <div><span className="inline-block w-3 h-3 rounded bg-red-500 mr-1.5" />High activity (paint, baseline)</div>
                        <div><span className="inline-block w-3 h-3 rounded bg-orange-400 mr-1.5" />Medium (free throw, wings)</div>
                        <div><span className="inline-block w-3 h-3 rounded bg-yellow-300 mr-1.5" />Low (perimeter)</div>
                    </div>
                </Card>

                {/* Injury Risks */}
                <Card className="bg-[#0d2847] border-white/10 p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Heart className="w-5 h-5 text-red-400" /> Injury Risk Analysis
                    </h3>
                    <div className="space-y-3" data-testid="injury-risks">
                        {SAMPLE.injuryRisks.map((r, i) => (
                            <div key={i} className="flex items-start justify-between p-3 bg-[#0a1628] rounded">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className={`w-4 h-4 mt-0.5 ${r.severity === 'high' ? 'text-red-400' : r.severity === 'medium' ? 'text-yellow-400' : 'text-green-400'}`} />
                                    <div>
                                        <div className="font-medium text-sm">{r.area}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{r.detail}</div>
                                    </div>
                                </div>
                                <SeverityBadge level={r.severity} />
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Recommendations */}
                <Card className="bg-gradient-to-br from-green-500/10 to-cyan-500/10 border-green-500/30 p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" /> AI Coach Recommendations
                    </h3>
                    <ul className="space-y-2" data-testid="recommendations">
                        {SAMPLE.recommendations.map((rec, i) => (
                            <li key={i} className="flex gap-3 text-sm">
                                <div className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                                <span>{rec}</span>
                            </li>
                        ))}
                    </ul>
                </Card>

                {/* CTA */}
                <Card className="bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 border-purple-500/40 p-6 text-center">
                    <Trophy className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
                    <h2 className="text-2xl font-bold mb-2">Want a report like this for yourself?</h2>
                    <p className="text-sm text-muted-foreground mb-4">Upload a 10-30 second clip and get your full performance breakdown in minutes.</p>
                    <div className="flex gap-2 justify-center flex-wrap">
                        <Link to="/video-analysis"><Button className="bg-purple-500 hover:bg-purple-600 text-white" data-testid="upload-cta">Upload Your Video</Button></Link>
                        <Link to="/how-it-works"><Button variant="outline" className="border-white/20" data-testid="learn-more-cta">Learn More</Button></Link>
                    </div>
                </Card>

                <div className="text-center text-xs text-muted-foreground py-4">
                    <FileText className="w-4 h-4 inline mr-1" /> Generated by NextGen Vision · Powered by Gemini Vision · MediaPipe BlazePose · YOLOv8
                </div>
            </div>
        </div>
    );
};

const StatTile = ({ label, value }) => (
    <div className="bg-[#0a1628] rounded p-3">
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
        <div className="text-xl font-bold">{value}</div>
    </div>
);

const CourtHeatmap = () => (
    <svg viewBox="0 0 600 320" className="w-full h-auto rounded border border-white/10 bg-gradient-to-b from-amber-900/40 to-amber-950/40">
        <defs>
            <radialGradient id="heatHigh" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.85" />
                <stop offset="60%" stopColor="#f59e0b" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="heatMed" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.7" />
                <stop offset="80%" stopColor="#fbbf24" stopOpacity="0" />
            </radialGradient>
        </defs>
        {/* Court outline */}
        <rect x="20" y="20" width="560" height="280" fill="none" stroke="#fbbf24" strokeWidth="2" />
        {/* Half-court line */}
        <line x1="300" y1="20" x2="300" y2="300" stroke="#fbbf24" strokeWidth="1.5" />
        <circle cx="300" cy="160" r="35" fill="none" stroke="#fbbf24" strokeWidth="1.5" />
        {/* Three-point arcs */}
        <path d="M 50 80 Q 50 160, 50 240 A 100 100 0 0 0 130 240 L 130 80 Z" fill="none" stroke="#fbbf24" strokeOpacity="0.5" strokeWidth="1" />
        <path d="M 550 80 Q 550 160, 550 240 A 100 100 0 0 1 470 240 L 470 80 Z" fill="none" stroke="#fbbf24" strokeOpacity="0.5" strokeWidth="1" />
        {/* Free throw lines */}
        <rect x="20" y="100" width="80" height="120" fill="none" stroke="#fbbf24" strokeOpacity="0.5" strokeWidth="1" />
        <rect x="500" y="100" width="80" height="120" fill="none" stroke="#fbbf24" strokeOpacity="0.5" strokeWidth="1" />
        {/* Heatmap blobs - use solid fill colors with opacity for reliability */}
        {[
            { cx: 80, cy: 160, r: 50, color: '#ef4444', op: 0.45 },
            { cx: 150, cy: 130, r: 35, color: '#f59e0b', op: 0.4 },
            { cx: 220, cy: 200, r: 40, color: '#f59e0b', op: 0.35 },
            { cx: 300, cy: 160, r: 30, color: '#ef4444', op: 0.5 },
            { cx: 380, cy: 130, r: 28, color: '#f59e0b', op: 0.4 },
            { cx: 480, cy: 180, r: 35, color: '#f59e0b', op: 0.4 },
        ].map((b, i) => (
            <circle key={i} cx={b.cx} cy={b.cy} r={b.r} fill={b.color} fillOpacity={b.op}>
                <animate attributeName="r" values={`${b.r};${b.r + 4};${b.r}`} dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            </circle>
        ))}
    </svg>
);

export default SampleReportPage;
